import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import threadsAPI from '../api/threads';
import sessionsAPI from '../api/sessions';
import usersAPI from '../api/users';
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const { userId, logout, user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('threads'); // 'threads' | 'conversations' | 'settings'
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createLabel, setCreateLabel] = useState('');
  const [createSystemPrompt, setCreateSystemPrompt] = useState('');
  const [createFirstMessage, setCreateFirstMessage] = useState('');
  const [createAllUsersToOneSession, setCreateAllUsersToOneSession] = useState(false);
  const [createIsActive, setCreateIsActive] = useState(true);
  const [createError, setCreateError] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  
  // Sessions state for conversations tab
  const [sessions, setSessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [sessionsError, setSessionsError] = useState('');
  const [threadOwners, setThreadOwners] = useState({}); // threadId -> owner data
  
  // Profile picture state
  const [isUploadingProfilePic, setIsUploadingProfilePic] = useState(false);
  const [profilePicError, setProfilePicError] = useState('');
  const [profilePicSuccess, setProfilePicSuccess] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoadingUser, setIsLoadingUser] = useState(false);

  const formatTimeAgo = (isoString) => {
    if (!isoString) return '';
    const now = new Date();
    const then = new Date(isoString);
    const seconds = Math.max(1, Math.floor((now.getTime() - then.getTime()) / 1000));

    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (seconds < 60) return `${seconds}s ago`;
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    if (weeks < 5) return `${weeks}w ago`;
    if (months < 12) return `${months}mo ago`;
    return `${years}y ago`;
  };

  const fetchThreads = async () => {
    if (!userId) return;
    setIsLoading(true);
    setError('');
    try {
      const data = await threadsAPI.getThreadsByOwner(userId);
      setThreads(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch threads:', err);
      setError('Failed to load threads');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSessions = async () => {
    if (!userId) return;
    setIsLoadingSessions(true);
    setSessionsError('');
    try {
      const data = await sessionsAPI.getSessionsByUser(userId);
      const sessionsData = Array.isArray(data) ? data : [];
      setSessions(sessionsData);
      
      // Fetch thread owner information for each unique thread
      const uniqueThreadIds = [...new Set(sessionsData.map(session => session.conversation_thread_id).filter(Boolean))];
      const ownersData = {};
      
      for (const threadId of uniqueThreadIds) {
        try {
          const ownerData = await usersAPI.getBasicUserByThreadId(threadId);
          ownersData[threadId] = ownerData;
        } catch (error) {
          console.error(`Failed to fetch thread owner for thread ${threadId}:`, error);
          // Set a fallback owner data
          ownersData[threadId] = { username: 'Unknown', id: threadId };
        }
      }
      
      setThreadOwners(ownersData);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
      setSessionsError('Failed to load sessions');
    } finally {
      setIsLoadingSessions(false);
    }
  };

  const fetchCurrentUser = async () => {
    if (!userId) return;
    setIsLoadingUser(true);
    try {
      const userData = await usersAPI.getUserById(userId);
      setCurrentUser(userData);
    } catch (err) {
      console.error('Failed to fetch current user:', err);
    } finally {
      setIsLoadingUser(false);
    }
  };

  useEffect(() => {
    fetchCurrentUser();
  }, [userId]);

  useEffect(() => {
    if (activeTab === 'threads') {
      fetchThreads();
    } else if (activeTab === 'conversations') {
      fetchSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, activeTab]);

  const resetCreateForm = () => {
    setCreateLabel('');
    setCreateSystemPrompt('');
    setCreateFirstMessage('');
    setCreateAllUsersToOneSession(false);
    setCreateIsActive(true);
    setCreateError('');
    setIsCreating(false);
  };

  const handleOpenCreate = () => {
    resetCreateForm();
    setIsCreateModalOpen(true);
  };

  const handleCloseCreate = () => {
    setIsCreateModalOpen(false);
  };

  const handleCreateThread = async (e) => {
    e.preventDefault();
    if (!userId) {
      setCreateError('You must be logged in to create a thread.');
      return;
    }
    setCreateError('');
    setIsCreating(true);
    try {
      const payload = {
        owner_id: userId,
        label: createLabel.trim() ? createLabel.trim() : null,
        system_prompt: createSystemPrompt.trim() ? createSystemPrompt.trim() : null,
        first_message: createFirstMessage.trim() ? createFirstMessage.trim() : null,
        all_users_to_one_session: createAllUsersToOneSession,
        is_active: createIsActive,
      };
      await threadsAPI.createThread(payload);
      await fetchThreads();
      setIsCreateModalOpen(false);
      resetCreateForm();
    } catch (err) {
      console.error('Failed to create thread:', err);
      setCreateError(err?.message || 'Failed to create thread');
    } finally {
      setIsCreating(false);
    }
  };

  const navItems = useMemo(() => ([
    { id: 'threads', label: 'Threads' },
    { id: 'conversations', label: 'My conversations' },
    { id: 'settings', label: 'Settings' },
  ]), []);

  const username = useMemo(() => {
    if (!user) return 'User';
    if (user.username) return user.username;
    if (user.email) return user.email;
    return 'User';
  }, [user]);

  const userInitials = useMemo(() => {
    const source = (user?.username || user?.email || 'U').trim();
    const parts = source.replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean);
    const letters = parts.length >= 2 ? parts[0][0] + parts[1][0] : source[0];
    return letters.toUpperCase();
  }, [user]);

  const personalThread = useMemo(() => {
    if (!Array.isArray(threads)) return null;
    return threads.find((t) => t && t.is_personal === true) || null;
  }, [threads]);

  const nonPersonalThreads = useMemo(() => {
    if (!Array.isArray(threads)) return [];
    return threads.filter((t) => !t?.is_personal);
  }, [threads]);

  // Group sessions by thread owner
  const sessionsByOwner = useMemo(() => {
    if (!Array.isArray(sessions) || Object.keys(threadOwners).length === 0) return [];
    
    const grouped = {};
    sessions.forEach(session => {
      const threadId = session.conversation_thread_id;
      const owner = threadOwners[threadId];
      
      if (owner) {
        const ownerKey = `${owner.id}-${owner.username}`;
        if (!grouped[ownerKey]) {
          grouped[ownerKey] = {
            owner,
            sessions: []
          };
        }
        grouped[ownerKey].sessions.push(session);
      }
    });
    
    // Convert to array and sort by owner username
    return Object.values(grouped).sort((a, b) => 
      (a.owner.username || '').localeCompare(b.owner.username || '')
    );
  }, [sessions, threadOwners]);

  const handleSelectTab = (tabId) => {
    setActiveTab(tabId);
    setIsSidebarOpen(false);
  };

  const handleProfilePictureUpload = async (file) => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setProfilePicError('Please select an image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setProfilePicError('File size must be less than 5MB');
      return;
    }
    
    setIsUploadingProfilePic(true);
    setProfilePicError('');
    setProfilePicSuccess('');
    
    try {
      const updatedUser = await usersAPI.updateProfilePicture(file);
      setCurrentUser(updatedUser);
      setProfilePicSuccess('Profile picture updated successfully!');
      // Clear success message after 3 seconds
      setTimeout(() => setProfilePicSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to update profile picture:', err);
      setProfilePicError(err?.message || 'Failed to update profile picture');
    } finally {
      setIsUploadingProfilePic(false);
    }
  };

  const handleRemoveProfilePicture = async () => {
    setIsUploadingProfilePic(true);
    setProfilePicError('');
    setProfilePicSuccess('');
    
    try {
      const updatedUser = await usersAPI.updateProfilePicture(null);
      setCurrentUser(updatedUser);
      setProfilePicSuccess('Profile picture removed successfully!');
      // Clear success message after 3 seconds
      setTimeout(() => setProfilePicSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to remove profile picture:', err);
      setProfilePicError(err?.message || 'Failed to remove profile picture');
    } finally {
      setIsUploadingProfilePic(false);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleProfilePictureUpload(file);
    }
    // Reset the input so the same file can be selected again
    e.target.value = '';
  };

  return (
    <div className="dashboard-root">
      {/* Mobile-only hamburger, hidden on desktop; also hidden when sidebar is open */}
      {!isSidebarOpen && (
        <button
          type="button"
          className="hamburger"
          aria-label="Open menu"
          aria-expanded={isSidebarOpen}
          onClick={() => setIsSidebarOpen(true)}
        >
          <span />
          <span />
          <span />
        </button>
      )}

      <div className="dashboard-shell">
        <aside className={`sidebar ${isSidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-profile">
            {currentUser?.profile_picture_url ? (
              <img 
                src={currentUser.profile_picture_url} 
                alt="Profile" 
                className="sidebar-avatar-image"
              />
            ) : (
              <div className="avatar-placeholder" aria-hidden>
                {userInitials}
              </div>
            )}
            <div className="sidebar-username" title={username}>{username}</div>
          </div>
          <div className="sidebar-section">
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`sidebar-item ${activeTab === item.id ? 'active' : ''}`}
                onClick={() => handleSelectTab(item.id)}
              >
                {item.label}
              </button>
            ))}
            <div style={{ height: 12 }} />
            <button type="button" className="sidebar-item" onClick={logout}>Logout</button>
          </div>
        </aside>

        {isSidebarOpen && (
          <div className="sidebar-backdrop" onClick={() => setIsSidebarOpen(false)} />
        )}

        <main className="dashboard-content">
          {activeTab === 'threads' && (
            <div className="dashboard-container">
              {error && <div className="error-message">{error}</div>}
              {isLoading ? (
                <div className="loading-inline">Loading threads...</div>
              ) : (
                <>
                  {personalThread && (
                    <div className="threads-grid personal-thread-container">
                      <div
                        className="thread-card personal-thread-card"
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate('/personal/thread')}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            navigate('/personal/thread');
                          }
                        }}
                      >
                        <div className="personal-badge" aria-hidden>Personal</div>
                        <div className="thread-card-body">
                          <div className="thread-title">{personalThread.label || 'Personal thread'}</div>
                          <div className="thread-meta">
                            <span className="thread-meta-item">
                              {(Array.isArray(personalThread.conversation_sessions) ? personalThread.conversation_sessions.length : 0)} session{(Array.isArray(personalThread.conversation_sessions) ? personalThread.conversation_sessions.length : 0) === 1 ? '' : 's'}
                            </span>
                            {personalThread.created_at && (
                              <span className="thread-meta-sep">•</span>
                            )}
                            {personalThread.created_at && (
                              <span className="thread-meta-item" title={new Date(personalThread.created_at).toLocaleString()}>
                                {formatTimeAgo(personalThread.created_at)}
                              </span>
                            )}
                            <span className="thread-meta-sep">•</span>
                            <span className={`thread-status ${personalThread.is_active ? 'active' : 'inactive'}`}>
                              {personalThread.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="threads-grid">
                  <button
                    type="button"
                    className="thread-card create-thread-card"
                    onClick={handleOpenCreate}
                  >
                    <div className="create-icon" aria-hidden>
                      +
                    </div>
                    <div className="create-text">Create thread</div>
                  </button>

                  {nonPersonalThreads.map((thread) => (
                    <div
                      key={thread.id}
                      className="thread-card"
                      role="button"
                      tabIndex={0}
                      onClick={() => window.open(`/thread/${thread.id}`, '_blank', 'noopener,noreferrer')}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          window.open(`/thread/${thread.id}`, '_blank', 'noopener,noreferrer');
                        }
                      }}
                    >
                      <div className="thread-card-body">
                        <div className="thread-title">{thread.label || 'Untitled thread'}</div>
                        <div className="thread-meta">
                          <span className="thread-meta-item">
                            {(Array.isArray(thread.conversation_sessions) ? thread.conversation_sessions.length : 0)} session{(Array.isArray(thread.conversation_sessions) ? thread.conversation_sessions.length : 0) === 1 ? '' : 's'}
                          </span>
                          {thread.created_at && (
                            <span className="thread-meta-sep">•</span>
                          )}
                          {thread.created_at && (
                            <span className="thread-meta-item" title={new Date(thread.created_at).toLocaleString()}>
                              {formatTimeAgo(thread.created_at)}
                            </span>
                          )}
                          <span className="thread-meta-sep">•</span>
                          <span className={`thread-status ${thread.is_active ? 'active' : 'inactive'}`}>
                            {thread.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'conversations' && (
            <div className="dashboard-container">
              <h2 className="screen-title">My conversations</h2>
              {sessionsError && <div className="error-message">{sessionsError}</div>}
              {isLoadingSessions ? (
                <div className="loading-inline">Loading conversations...</div>
              ) : sessionsByOwner.length === 0 ? (
                <div className="screen-blank">No conversations yet. Start a conversation from a thread!</div>
              ) : (
                <div className="conversations-by-owner">
                  {sessionsByOwner.map((ownerGroup) => (
                    <div key={ownerGroup.owner.id} className="owner-section">
                      <div className="owner-section-header">
                        <div 
                          className="owner-avatar clickable-avatar"
                          role="button"
                          tabIndex={0}
                          onClick={() => window.open(`/user/${ownerGroup.owner.id}/${ownerGroup.owner.username || 'user'}`, '_blank', 'noopener,noreferrer')}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              window.open(`/user/${ownerGroup.owner.id}/${ownerGroup.owner.username || 'user'}`, '_blank', 'noopener,noreferrer');
                            }
                          }}
                          title="View user profile"
                        >
                          {ownerGroup.owner.profile_picture_url ? (
                            <img 
                              src={ownerGroup.owner.profile_picture_url} 
                              alt="Profile" 
                              className="owner-avatar-image"
                            />
                          ) : (
                            <div className="avatar-initial">
                              {ownerGroup.owner.username ? ownerGroup.owner.username.charAt(0).toUpperCase() : 'U'}
                            </div>
                          )}
                        </div>
                        <div className="owner-info">
                          <div className="owner-username">{ownerGroup.owner.username || 'Unknown'}</div>
                          <div className="owner-session-count">
                            {ownerGroup.sessions.length} session{ownerGroup.sessions.length === 1 ? '' : 's'}
                          </div>
                        </div>
                      </div>
                      <div className="threads-grid">
                        {ownerGroup.sessions.map((session) => (
                          <div
                            key={session.id}
                            className="thread-card"
                            role="button"
                            tabIndex={0}
                            onClick={() => window.open(`/chat/session/${session.hash_id}/thread/${session.conversation_thread_id}`, '_blank', 'noopener,noreferrer')}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                window.open(`/chat/session/${session.hash_id}/thread/${session.conversation_thread_id}`, '_blank', 'noopener,noreferrer');
                              }
                            }}
                          >
                            <div className="thread-card-body">
                              <div className="thread-title">{session.label || 'Untitled session'}</div>
                              <div className="thread-subtitle">{formatTimeAgo(session.created_at)}</div>
                              {Array.isArray(session.users) && session.users.length > 0 && (
                                <div className="pill-row" aria-label="session users">
                                  {session.users.map((u) => (
                                    <span key={u.id} className="pill">
                                      <div className="pill-avatar">
                                        {u.username ? u.username.charAt(0).toUpperCase() : 'U'}
                                      </div>
                                      {u.username}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="screen-container">
              <h2 className="screen-title">Settings</h2>
              
              <div className="settings-section">
                <h3 className="settings-section-title">Profile Picture</h3>
                <div className="profile-picture-container">
                  <div className="current-profile-picture">
                    {currentUser?.profile_picture_url ? (
                      <img 
                        src={currentUser.profile_picture_url} 
                        alt="Profile" 
                        className="profile-picture-image"
                      />
                    ) : (
                      <div className="profile-picture-placeholder">
                        {userInitials}
                      </div>
                    )}
                  </div>
                  
                  <div className="profile-picture-actions">
                    <input
                      type="file"
                      id="profile-picture-upload"
                      accept="image/*"
                      onChange={handleFileInputChange}
                      style={{ display: 'none' }}
                      disabled={isUploadingProfilePic}
                    />
                    <label 
                      htmlFor="profile-picture-upload" 
                      className="btn btn-primary profile-picture-btn"
                    >
                      {isUploadingProfilePic ? 'Uploading...' : 'Upload Picture'}
                    </label>
                    
                    {currentUser?.profile_picture_url && (
                      <button
                        type="button"
                        className="btn btn-secondary profile-picture-btn"
                        onClick={handleRemoveProfilePicture}
                        disabled={isUploadingProfilePic}
                      >
                        Remove Picture
                      </button>
                    )}
                  </div>
                  
                  {profilePicError && (
                    <div className="profile-picture-error">{profilePicError}</div>
                  )}
                  
                  {profilePicSuccess && (
                    <div className="profile-picture-success">{profilePicSuccess}</div>
                  )}
                  
                  <div className="profile-picture-help">
                    <p>Upload a profile picture to personalize your account.</p>
                    <p>Supported formats: JPG, PNG, GIF. Max size: 5MB.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {isCreateModalOpen && (
        <div className="modal-backdrop" onClick={handleCloseCreate}>
          <div className="modal modal-lg create-thread-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Create Thread</div>
              <button className="modal-close" onClick={handleCloseCreate}>×</button>
            </div>
            <div className="modal-content">
              {createError && <div className="form-error">{createError}</div>}
              <form onSubmit={handleCreateThread} className="form create-thread-form">
                <div className="form-row">
                  <label htmlFor="thread-label" className="form-label">Label (optional)</label>
                  <input
                    id="thread-label"
                    type="text"
                    className="form-input"
                    placeholder="e.g., Research: RAG evaluation plan"
                    value={createLabel}
                    onChange={(e) => setCreateLabel(e.target.value)}
                    maxLength={500}
                    disabled={isCreating}
                  />
                  <div className="form-helper">Up to 500 characters.</div>
                </div>

                <div className="form-row">
                  <label htmlFor="thread-system-prompt" className="form-label">System prompt (optional)</label>
                  <textarea
                    id="thread-system-prompt"
                    className="form-textarea"
                    placeholder="Provide instructions for how the assistant should behave..."
                    value={createSystemPrompt}
                    onChange={(e) => setCreateSystemPrompt(e.target.value)}
                    rows={10}
                    disabled={isCreating}
                  />
                  <div className="form-helper">Long-form guidance that applies to this thread.</div>
                </div>

                <div className="form-row form-row-with-checkboxes">
                  <div className="form-text-field">
                    <label htmlFor="thread-first-message" className="form-label">First message (optional)</label>
                    <textarea
                      id="thread-first-message"
                      className="form-textarea"
                      placeholder="Initial message to be sent by assistant in new sessions..."
                      value={createFirstMessage}
                      onChange={(e) => setCreateFirstMessage(e.target.value)}
                      rows={4}
                      disabled={isCreating}
                    />
                    <div className="form-helper">Message that will be sent automatically when a new session starts.</div>
                  </div>
                  <div className="form-checkboxes">
                    <div className="form-checkbox-group">
                      <div className="form-checkbox-row">
                        <input
                          type="checkbox"
                          id="thread-all-users-to-one-session"
                          checked={createAllUsersToOneSession}
                          onChange={(e) => setCreateAllUsersToOneSession(e.target.checked)}
                          disabled={isCreating}
                        />
                        <label htmlFor="thread-all-users-to-one-session" className="form-checkbox-label">
                          All users to one session
                        </label>
                      </div>
                      <div className="form-helper">Allow multiple users to join in one conversation session.</div>
                    </div>
                    <div className="form-checkbox-group">
                      <div className="form-checkbox-row">
                        <input
                          type="checkbox"
                          id="thread-is-active"
                          checked={createIsActive}
                          onChange={(e) => setCreateIsActive(e.target.checked)}
                          disabled={isCreating}
                        />
                        <label htmlFor="thread-is-active" className="form-checkbox-label">
                          Active thread
                        </label>
                      </div>
                      <div className="form-helper">Thread is active and can be used for conversations.</div>
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseCreate} disabled={isCreating}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={isCreating}>
                    {isCreating ? 'Creating…' : 'Create thread'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;


